import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Volume2, Loader2, AlertCircle, Pause, Play, Square, Mic } from 'lucide-react';
import { apiRequest, getMediaUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Voice narration control.
 *
 * Renders TWO small icon triggers side-by-side — one per provider
 * (ElevenLabs + Fish Audio). Only enabled if the corresponding
 * API key is configured. When audio is ready, a Play / Pause / Stop
 * control appears next to the triggers.
 *
 * Async job pattern — POST returns job_id, polls until ready.
 * Per-section audio URLs are cached on the profile.
 *
 * Props:
 *   text        — text to narrate (required)
 *   profileId   — optional profile id for caching
 *   sectionKey  — optional section identifier for per-section cache
 *   existingUrl — previously-cached URL (auto-loads)
 */
const POLL_INTERVAL_MS = 1500;
const POLL_MAX_ATTEMPTS = 80;

const VoiceBriefingButton = ({
  text,
  profileId = null,
  sectionKey = null,
  existingUrl = null,
  language = null,
  className = '',
}) => {
  const { token } = useAuth();
  const [status, setStatus] = useState({ loading: true, elevenlabs: false, fish: false });
  const [audioUrl, setAudioUrl] = useState(existingUrl || null);
  const [activeProvider, setActiveProvider] = useState(null); // which one is running
  const [busyMsg, setBusyMsg] = useState('');
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [notice, setNotice] = useState('');
  const audioRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => { setAudioUrl(existingUrl || null); }, [existingUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiRequest('/api/voice/status');
        if (cancelled) return;
        setStatus({
          loading: false,
          elevenlabs: !!data.elevenlabs_configured,
          fish: !!data.fish_audio_configured,
        });
      } catch {
        if (!cancelled) setStatus({ loading: false, elevenlabs: false, fish: false });
      }
    })();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const poll = useCallback((jobId, attempts = 0) => {
    if (attempts >= POLL_MAX_ATTEMPTS) {
      setActiveProvider(null);
      setError('Audio generation timed out. Try again.');
      return;
    }
    pollTimerRef.current = setTimeout(async () => {
      try {
        const job = await apiRequest(`/api/voice/tts/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (job.status === 'ready') {
          setAudioUrl(job.audio_url);
          setActiveProvider(null);
          setBusyMsg('');
          setNotice(job.fallback_reason || '');
          setTimeout(() => {
            if (audioRef.current) { audioRef.current.load(); audioRef.current.play().catch(() => {}); }
          }, 60);
        } else if (job.status === 'failed') {
          setActiveProvider(null);
          setBusyMsg('');
          setError(job.error || 'Audio generation failed.');
        } else {
          setBusyMsg(`${Math.floor((attempts * POLL_INTERVAL_MS) / 1000)}s`);
          poll(jobId, attempts + 1);
        }
      } catch (exc) {
        setActiveProvider(null);
        setError(exc.message || 'Failed to check audio status.');
      }
    }, POLL_INTERVAL_MS);
  }, [token]);

  const generate = useCallback(async (provider) => {
    if (!text || !text.trim()) return;
    setError('');
    setNotice('');
    setActiveProvider(provider);
    setBusyMsg('...');
    try {
      const data = await apiRequest('/api/voice/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, profile_id: profileId, section_key: sectionKey, provider, language }),
      });
      poll(data.job_id, 0);
    } catch (exc) {
      setActiveProvider(null);
      setBusyMsg('');
      setError(exc.message || 'Failed to start audio generation.');
    }
  }, [text, profileId, sectionKey, language, token, poll]);

  const handlePlay = () => {
    if (audioRef.current) audioRef.current.play().catch(() => {});
  };
  const handlePause = () => {
    if (audioRef.current) audioRef.current.pause();
  };
  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  };

  if (status.loading) return null;

  if (!status.elevenlabs && !status.fish) {
    return (
      <div className={`voice-briefing voice-briefing--unconfigured ${className}`} data-testid="voice-briefing-unconfigured">
        <AlertCircle size={12} />
        <span>Add <strong>ELEVENLABS_API_KEY</strong> or <strong>FISH_AUDIO_API_KEY</strong> in backend/.env</span>
      </div>
    );
  }

  const isBusy = !!activeProvider;

  return (
    <div className={`voice-briefing voice-briefing--compact ${className}`} data-testid="voice-briefing">
      {/* Provider triggers */}
      <div className="voice-providers" role="group" aria-label="Voice provider">
        {status.elevenlabs && (
          <button
            type="button"
            className={`voice-provider-btn eleven ${activeProvider === 'elevenlabs' ? 'active' : ''}`}
            onClick={() => generate('elevenlabs')}
            disabled={isBusy || !text}
            title="Generate with ElevenLabs (Sergio)"
            aria-label="ElevenLabs"
            data-testid="voice-btn-elevenlabs"
          >
            {activeProvider === 'elevenlabs' ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Volume2 size={14} />
            )}
            {activeProvider === 'elevenlabs' && busyMsg && (
              <span className="voice-provider-timer">{busyMsg}</span>
            )}
          </button>
        )}
        {status.fish && (
          <button
            type="button"
            className={`voice-provider-btn fish ${activeProvider === 'fish_audio' ? 'active' : ''}`}
            onClick={() => generate('fish_audio')}
            disabled={isBusy || !text}
            title="Generate with Fish Audio"
            aria-label="Fish Audio"
            data-testid="voice-btn-fish"
          >
            {activeProvider === 'fish_audio' ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Mic size={14} />
            )}
            {activeProvider === 'fish_audio' && busyMsg && (
              <span className="voice-provider-timer">{busyMsg}</span>
            )}
          </button>
        )}
      </div>

      {/* Playback controls (visible once audio is ready) */}
      {audioUrl && (
        <div className="voice-playback" data-testid="voice-playback">
          {!playing ? (
            <button
              type="button"
              className="voice-ctrl-btn play"
              onClick={handlePlay}
              title="Play"
              aria-label="Play audio"
              data-testid="voice-ctrl-play"
            >
              <Play size={14} />
            </button>
          ) : (
            <button
              type="button"
              className="voice-ctrl-btn pause"
              onClick={handlePause}
              title="Pause"
              aria-label="Pause audio"
              data-testid="voice-ctrl-pause"
            >
              <Pause size={14} />
            </button>
          )}
          <button
            type="button"
            className="voice-ctrl-btn stop"
            onClick={handleStop}
            title="Stop"
            aria-label="Stop audio"
            data-testid="voice-ctrl-stop"
          >
            <Square size={12} />
          </button>
          <audio
            ref={audioRef}
            src={getMediaUrl(audioUrl)}
            preload="metadata"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            data-testid="voice-audio-element"
          />
        </div>
      )}

      {error && (
        <p className="voice-briefing-error" data-testid="voice-briefing-error">
          <AlertCircle size={11} /> {error}
        </p>
      )}

      {notice && !error && (
        <p className="voice-briefing-notice" data-testid="voice-briefing-notice" style={{ fontSize: 11, color: 'rgba(252, 211, 77, 0.9)', margin: '4px 0 0', display: 'flex', gap: 4, alignItems: 'center' }}>
          <AlertCircle size={10} /> {notice}
        </p>
      )}
    </div>
  );
};

export default VoiceBriefingButton;
